import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';

const PROD_ORDERS_KEY = ['manufacturing', 'production-orders'] as const;

/* ── Types ── */

export interface ProductionOrder {
  id: string;
  productId: string;
  productName: string;
  orderNumber: string;
  status: string;
  plannedQuantity: number;
  producedQuantity: number;
  wasteQuantity: number;
  bomId?: string | null;
  bomVersion?: number | null;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  actualStart?: string | null;
  actualEnd?: string | null;
  costSummary?: any;
  notes?: string | null;
  /** Componentes da OP — presente no detalhe (`GET /manufacturing/orders/:id`),
   * ausente na listagem. */
  materialRequirements?: ProductionOrderMaterialRequirement[];
  createdAt: string;
  updatedAt: string;
}

/** Espelha `ProductionOrderMaterialRequirementDto` — presente no detalhe da OP
 * (`GET /manufacturing/orders/:id`), nao na listagem. */
export interface ProductionOrderMaterialRequirement {
  productId: string;
  productName?: string;
  /** Quantidade necessaria (do BOM x qtd planejada). */
  requiredQuantity: number;
  /** Quantidade ja consumida. */
  consumedQuantity: number;
  returnedQuantity: number;
  /** Estoque disponivel (cross-context inventory, agregado por produto). */
  availableStock: number;
  unit: string;
  unitCost: number;
}

export interface CreateProductionOrderInput {
  productId: string;
  productName: string;
  orderNumber: string;
  branchId?: string;
  plannedQuantity: number;
  scheduledStart: string;
  scheduledEnd: string;
  useActiveBom?: boolean;
  notes?: string;
}

export interface PlanOrderInput {
  scheduledStart: string;
  scheduledEnd: string;
  bomId: string;
}

export interface ConsumeMaterialInput {
  productId: string;
  quantity: number;
  unitCost: number;
  lotNumber?: string;
  notes?: string;
}

export interface ReturnMaterialInput {
  productId: string;
  quantity: number;
  lotNumber?: string;
  notes?: string;
}

export interface PostProductionInput {
  producedQuantity: number;
  wasteQuantity: number;
  unitCost: number;
  lotNumber?: string;
  serialNumber?: string;
  notes?: string;
}

export interface CancelOrderInput {
  justification: string;
}

/** Espelha `OrderProgressResponseDto`. */
export interface OrderProgressMaterial {
  productId: string;
  productName?: string;
  requiredQuantity: number;
  consumedQuantity: number;
  consumptionPercent: number;
  unit: string;
}

export interface OrderProgress {
  orderId: string;
  orderNumber: string;
  productId: string;
  productName: string;
  status: string;
  plannedQuantity: number;
  producedQuantity: number;
  wasteQuantity: number;
  /** Rendimento: produzido sobre planejado, em %. */
  yield_: number;
  materialConsumptionPercent: number;
  materialRequirements: OrderProgressMaterial[];
  actualStart?: string | null;
  actualEnd?: string | null;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  isOverdue: boolean;
}

/**
 * Falta de material: alguma linha consumida abaixo do previsto numa ordem que
 * ja esta em producao. Derivado — nao ha campo `hasMaterialShortage`.
 */
export function hasMaterialShortage(progress: OrderProgress): boolean {
  if (progress.status !== 'IN_PROGRESS' && progress.status !== 'RELEASED') return false;
  return progress.materialRequirements.some(
    (m) => m.consumedQuantity < m.requiredQuantity,
  );
}

export interface CostSummary {
  materialCost: number;
  expectedCost: number;
  variance: number;
  variancePercent: number;
  costPerUnit: number;
}

export interface Posting {
  id: string;
  productionOrderId: string;
  type: string;
  productId: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
  lotNumber?: string;
  serialNumber?: string;
  postedById: string;
  notes?: string;
  createdAt: string;
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Contagens do chao de fabrica que acompanham a listagem.
 *
 * Vem no mesmo envelope porque a tela mostra os quatro indicadores junto da
 * fila: pedir numa segunda chamada faria o painel e a lista discordarem por um
 * instante a cada filtro.
 */
export type ProductionOrderStatusKey =
  | 'DRAFT'
  | 'PLANNED'
  | 'RELEASED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'CLOSED'
  | 'CANCELLED';

export interface ProductionOrderCounts {
  /* Chave fechada, nao `Record<string, number>`: com assinatura de indice todo
     acesso volta `number | undefined` e o painel precisaria de `?? 0` em cada
     indicador — mascarando a diferenca entre "zero ordens" e "status que o
     servidor nao mandou". O backend devolve os sete status sempre. */
  byStatus: Record<ProductionOrderStatusKey, number>;
  /** Ordens abertas cujo fim planejado ja passou. */
  delayed: number;
  /** Concluidas ou fechadas nos ultimos 7 dias. */
  completedSince: number;
}

interface ProductionOrderList extends PaginatedResponse<ProductionOrder> {
  counts: ProductionOrderCounts;
}

/* ── Queries ── */

export function useProductionOrders(params?: {
  status?: string;
  productId?: string;
  orderNumber?: string;
  search?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: [...PROD_ORDERS_KEY, params],
    queryFn: () =>
      apiClient.get<ProductionOrderList>('/manufacturing/orders', {
        params: params as Record<string, string>,
      }),
  });
}

export function useProductionOrder(id: string | undefined) {
  return useQuery({
    queryKey: [...PROD_ORDERS_KEY, id],
    queryFn: () => apiClient.get<ProductionOrder>(`/manufacturing/orders/${id}`),
    enabled: !!id,
  });
}

export function useProductionOrderProgress(id: string | undefined) {
  return useQuery({
    queryKey: [...PROD_ORDERS_KEY, id, 'progress'],
    queryFn: () => apiClient.get<OrderProgress>(`/manufacturing/orders/${id}/progress`),
    enabled: !!id,
  });
}

export function useProductionOrderCosts(id: string | undefined) {
  return useQuery({
    queryKey: [...PROD_ORDERS_KEY, id, 'costs'],
    queryFn: () => apiClient.get<CostSummary>(`/manufacturing/orders/${id}/costs`),
    enabled: !!id,
  });
}

export function useProductionOrderPostings(id: string | undefined) {
  return useQuery({
    queryKey: [...PROD_ORDERS_KEY, id, 'postings'],
    queryFn: () => apiClient.get<Posting[]>(`/manufacturing/orders/${id}/postings`),
    enabled: !!id,
  });
}

/* ── Mutations ── */

export function useCreateProductionOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductionOrderInput) =>
      apiClient.post<ProductionOrder>('/manufacturing/orders', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROD_ORDERS_KEY });
    },
  });
}

export function useUpdateProductionOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & Partial<CreateProductionOrderInput>) =>
      apiClient.put<ProductionOrder>(`/manufacturing/orders/${id}`, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: PROD_ORDERS_KEY });
      qc.invalidateQueries({ queryKey: [...PROD_ORDERS_KEY, vars.id] });
    },
  });
}

export function usePlanProductionOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & PlanOrderInput) =>
      apiClient.post<ProductionOrder>(`/manufacturing/orders/${id}/plan`, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: PROD_ORDERS_KEY });
      qc.invalidateQueries({ queryKey: [...PROD_ORDERS_KEY, vars.id] });
    },
  });
}

export function useReleaseProductionOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, bomId }: { id: string; bomId: string }) =>
      apiClient.post<ProductionOrder>(`/manufacturing/orders/${id}/release`, { bomId }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: PROD_ORDERS_KEY });
      qc.invalidateQueries({ queryKey: [...PROD_ORDERS_KEY, vars.id] });
    },
  });
}

export function useConsumeMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & ConsumeMaterialInput) =>
      apiClient.post(`/manufacturing/orders/${id}/consume`, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: PROD_ORDERS_KEY });
      qc.invalidateQueries({
        queryKey: [...PROD_ORDERS_KEY, vars.id, 'progress'],
      });
      qc.invalidateQueries({
        queryKey: [...PROD_ORDERS_KEY, vars.id, 'postings'],
      });
      qc.invalidateQueries({
        queryKey: [...PROD_ORDERS_KEY, vars.id, 'costs'],
      });
    },
  });
}

export function useReturnMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & ReturnMaterialInput) =>
      apiClient.post(`/manufacturing/orders/${id}/return`, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: PROD_ORDERS_KEY });
      qc.invalidateQueries({
        queryKey: [...PROD_ORDERS_KEY, vars.id, 'progress'],
      });
      qc.invalidateQueries({
        queryKey: [...PROD_ORDERS_KEY, vars.id, 'postings'],
      });
    },
  });
}

export function usePostProductionOutput() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & PostProductionInput) =>
      apiClient.post(`/manufacturing/orders/${id}/post-output`, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: PROD_ORDERS_KEY });
      qc.invalidateQueries({
        queryKey: [...PROD_ORDERS_KEY, vars.id, 'progress'],
      });
      qc.invalidateQueries({
        queryKey: [...PROD_ORDERS_KEY, vars.id, 'postings'],
      });
      qc.invalidateQueries({
        queryKey: [...PROD_ORDERS_KEY, vars.id, 'costs'],
      });
    },
  });
}

export function useCloseProductionOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`/manufacturing/orders/${id}/close`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: PROD_ORDERS_KEY });
      qc.invalidateQueries({ queryKey: [...PROD_ORDERS_KEY, id] });
      qc.invalidateQueries({ queryKey: [...PROD_ORDERS_KEY, id, 'costs'] });
    },
  });
}

export function useCancelProductionOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, justification }: { id: string; justification: string }) =>
      apiClient.post(`/manufacturing/orders/${id}/cancel`, { justification }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: PROD_ORDERS_KEY });
      qc.invalidateQueries({ queryKey: [...PROD_ORDERS_KEY, vars.id] });
    },
  });
}
